import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../hooks/useAuth';
import type { DpoContact } from '@shared/types';

interface DpoContactInfoProps {
  /** Compact one-line layout (e.g. for footers). Default: full card. */
  compact?: boolean;
  className?: string;
  /** Override schoolId (for anonymous/public pages before auth). */
  schoolIdOverride?: string;
}

interface DpoState {
  dpo?: DpoContact;
  legacyName?: string;
}

export function DpoContactInfo({ compact = false, className = '', schoolIdOverride }: DpoContactInfoProps) {
  const { t } = useTranslation();
  const { customClaims } = useAuth();
  const schoolId = schoolIdOverride || customClaims?.schoolId;
  const [state, setState] = useState<DpoState>({});

  useEffect(() => {
    if (!schoolId) return;
    // Public projection (works pre-auth on anonymous pages) — DPO contact only,
    // no longer the full auth-gated schools/{id} doc. [C2 rules audit]
    const unsub = onSnapshot(doc(db, 'schoolPublic', schoolId), (snap) => {
      const data = snap.data();
      setState({
        dpo: data?.dpo,
        legacyName: data?.dataProtectionOfficerLegacy,
      });
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [schoolId]);

  const name = state.dpo?.name || state.legacyName;
  const email = state.dpo?.email;

  if (!name && !email) return null;

  if (compact) {
    return (
      <p className={`text-xs text-gray-600 ${className}`}>
        <strong>{t('dpo.contact')}:</strong> {name}
        {email && (
          <>
            {' — '}
            <a href={`mailto:${email}`} className="text-primary-600 hover:underline">
              {email}
            </a>
          </>
        )}
      </p>
    );
  }

  return (
    <div className={`border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm ${className}`}>
      <p className="font-medium text-gray-900 mb-1">{t('dpo.title')}</p>
      <p className="text-gray-700">{name}</p>
      {email && (
        <p className="text-gray-700">
          <a href={`mailto:${email}`} className="text-primary-600 hover:underline">
            {email}
          </a>
        </p>
      )}
      {state.dpo?.phone && <p className="text-gray-700">{state.dpo.phone}</p>}
      {state.dpo?.registryNumber && (
        <p className="text-xs text-gray-500 mt-1">
          {t('dpo.registryLabel')}: {state.dpo.registryNumber}
        </p>
      )}
    </div>
  );
}
